import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Moon, Sun } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fundingbridge/artifacts/6f4haw8y_9161E8E4-2A18-454E-B39C-D1A5E84AD155.png';

const Privacy = () => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
      {/* Header */}
      <header className="glass-effect border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="Our Cap Logo" className="h-12 w-auto object-contain" />
            </Link>
            <Button onClick={toggleTheme} variant="outline" size="sm" className="p-2">
              {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-slate-700" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-700 dark:text-slate-300">
          <p className="text-lg">
            <strong>Effective Date:</strong> January 1, 2026
          </p>

          <p>
            Our Cap ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains 
            how we collect, use, disclose, and safeguard your information when you use our Platform.
          </p>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-slate-800 dark:text-slate-200 mt-6 mb-3">Personal Information</h3>
            <p>We collect information you provide directly, including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Name, email address, and phone number</li>
              <li>Account credentials</li>
              <li>Business entity information</li>
              <li>Financial information related to deals</li>
              <li>Bank statements and verification documents</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-800 dark:text-slate-200 mt-6 mb-3">Automatically Collected Information</h3>
            <p>We automatically collect:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Usage patterns and Platform interactions</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">2. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Provide and maintain our Platform services</li>
              <li>Process transactions and send related information</li>
              <li>Verify LSP liquidity and Borrower eligibility</li>
              <li>Communicate with you about your account and deals</li>
              <li>Improve our Platform and develop new features</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or illegal activity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">3. Information Sharing</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Service Providers:</strong> Third parties who assist in Platform operations (payment processors, verification services)</li>
              <li><strong>Admin Team:</strong> Our internal team for coordination and support</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
            </ul>
            <p className="mt-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <strong>Privacy Between Users:</strong> We maintain strict confidentiality between Borrowers and LSPs. 
              Identifying information is not shared between these parties without explicit consent and admin approval.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">4. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments</li>
              <li>Access controls and audit logging</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">5. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
              Privacy Policy, unless a longer retention period is required by law. Financial records may be retained 
              for up to 7 years for compliance purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">6. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>
            <p className="mt-4">To exercise these rights, contact us at privacy@ourcap.com.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">7. Third-Party Services</h2>
            <p>Our Platform integrates with third-party services including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Stripe:</strong> For payment processing</li>
              <li><strong>Plaid:</strong> For bank account verification</li>
              <li><strong>DocuSign:</strong> For electronic signatures</li>
            </ul>
            <p className="mt-4">These services have their own privacy policies governing data use.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">8. Cookies</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze usage, and remember 
              preferences. You can control cookie settings through your browser.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">9. Children's Privacy</h2>
            <p>
              Our Platform is not intended for individuals under 18 years of age. We do not knowingly collect 
              personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by 
              posting a notice on our Platform or sending you an email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mt-8 mb-4">11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us:</p>
            <p className="mt-2">
              <strong>Email:</strong> privacy@ourcap.com<br />
              <strong>Address:</strong> Our Cap LLC, Business Address
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <img src={LOGO_URL} alt="Our Cap Logo" className="h-12 w-auto mx-auto mb-4 object-contain" />
            <p className="text-sm text-slate-600 dark:text-slate-400">&copy; {new Date().getFullYear()} Our Cap. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
